/**
 * Tracks the browser's online/offline state and exposes a brief "just reconnected"
 * window so callers can flash a confirmation before hiding the offline banner.
 */

import { useEffect, useState } from 'react'

const RECONNECT_FLASH_MS = 2500

export interface OnlineStatus {
  isOnline: boolean
  /** True for a short pulse right after going from offline back to online. */
  justReconnected: boolean
}

export function useOnlineStatus(): OnlineStatus {
  const [isOnline, setIsOnline] = useState(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine,
  )
  const [justReconnected, setJustReconnected] = useState(false)

  useEffect(() => {
    const onOnline = () => {
      setIsOnline(true)
      setJustReconnected(true)
      const timer = window.setTimeout(() => setJustReconnected(false), RECONNECT_FLASH_MS)
      return () => window.clearTimeout(timer)
    }
    const onOffline = () => setIsOnline(false)
    window.addEventListener('online', onOnline)
    window.addEventListener('offline', onOffline)
    return () => {
      window.removeEventListener('online', onOnline)
      window.removeEventListener('offline', onOffline)
    }
  }, [])

  return { isOnline, justReconnected }
}
